import { useCallback, useEffect, useState } from "react";
import {
  abandonManufacturingBomAuditSession,
  completeManufacturingBomAuditSession,
  getManufacturingBomAuditSession,
  listManufacturingBomAuditSessions,
  openManufacturingBomAuditSession,
  reopenManufacturingBomAuditSession,
  saveManufacturingBomAuditNode,
  startManufacturingBomAuditSubAudit,
} from "@/lib/manufacturing/bomAuditClient";
import type {
  MfgBomAuditKind,
  MfgBomAuditNode,
  MfgBomAuditNodeResult,
  MfgBomAuditSession,
} from "@/lib/manufacturing/types/bomAudit";

function errMsg(res: { ok: false; error?: unknown } | { ok: true; data: { ok?: boolean; error?: unknown } }): string {
  if (!res.ok) return String(res.error ?? "Request failed");
  if ("error" in res.data && res.data.error) return String(res.data.error);
  return "Request failed";
}

export function useManufacturingBomAudit(opts: { enabled: boolean }) {
  const [sessions, setSessions] = useState<MfgBomAuditSession[]>([]);
  const [session, setSession] = useState<MfgBomAuditSession | null>(null);
  const [nodes, setNodes] = useState<MfgBomAuditNode[]>([]);
  const [stack, setStack] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionNotes, setSessionNotes] = useState("");

  const refreshList = useCallback(async () => {
    const res = await listManufacturingBomAuditSessions({
      status: ["open", "partial", "abandoned"],
      roots_only: true,
    });
    if (!res.ok || !res.data.ok) {
      setError(errMsg(res as { ok: false; error?: unknown }));
      return;
    }
    setSessions(res.data.sessions);
  }, []);

  const loadSession = useCallback(async (sessionId: string, pushStack = false) => {
    setBusy(true);
    setError(null);
    try {
      const res = await getManufacturingBomAuditSession(sessionId);
      if (!res.ok || !res.data.ok) {
        setError(errMsg(res as { ok: false; error?: unknown }));
        return;
      }
      setSession(res.data.session);
      setNodes(res.data.nodes);
      setSessionNotes(res.data.session.notes ?? "");
      setStack((prev) => {
        if (!pushStack) return prev.length ? prev : [sessionId];
        if (prev[prev.length - 1] === sessionId) return prev;
        return [...prev, sessionId];
      });
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!opts.enabled) return;
    void refreshList();
  }, [opts.enabled, refreshList]);

  const openAudit = useCallback(
    async (payload: {
      recipe_id: string;
      audit_kind: MfgBomAuditKind;
      plant_id?: string | null;
      notes?: string;
    }) => {
      setBusy(true);
      setError(null);
      try {
        const res = await openManufacturingBomAuditSession(payload);
        if (!res.ok || !res.data.ok) {
          setError(errMsg(res as { ok: false; error?: unknown }));
          return false;
        }
        setSession(res.data.session);
        setNodes(res.data.nodes);
        setSessionNotes(res.data.session.notes ?? "");
        setStack([res.data.session.id]);
        await refreshList();
        return true;
      } finally {
        setBusy(false);
      }
    },
    [refreshList]
  );

  const saveNode = useCallback(
    async (node_id: string, patch: { result?: MfgBomAuditNodeResult; notes?: string }) => {
      setBusy(true);
      setError(null);
      try {
        const res = await saveManufacturingBomAuditNode({
          node_id,
          ...patch,
          session_notes: sessionNotes,
        });
        if (!res.ok || !res.data.ok) {
          setError(errMsg(res as { ok: false; error?: unknown }));
          return false;
        }
        setSession(res.data.session);
        setNodes((prev) =>
          prev.map((n) => (n.id === res.data.node.id ? res.data.node : n))
        );
        return true;
      } finally {
        setBusy(false);
      }
    },
    [sessionNotes]
  );

  const startSubAudit = useCallback(
    async (node_id: string) => {
      setBusy(true);
      setError(null);
      try {
        const res = await startManufacturingBomAuditSubAudit(node_id);
        if (!res.ok || !res.data.ok) {
          setError(errMsg(res as { ok: false; error?: unknown }));
          return false;
        }
        setSession(res.data.child_session);
        setNodes(res.data.child_nodes);
        setSessionNotes(res.data.child_session.notes ?? "");
        setStack((prev) => [...prev, res.data.child_session.id]);
        await refreshList();
        return true;
      } finally {
        setBusy(false);
      }
    },
    [refreshList]
  );

  const complete = useCallback(async () => {
    if (!session) return false;
    setBusy(true);
    setError(null);
    try {
      const res = await completeManufacturingBomAuditSession({
        session_id: session.id,
        notes: sessionNotes,
      });
      if (!res.ok || !res.data.ok) {
        setError(errMsg(res as { ok: false; error?: unknown }));
        return false;
      }
      // Pop to parent if this was a sub-audit
      const parentId = stack.length > 1 ? stack[stack.length - 2] : null;
      if (parentId) {
        setStack((prev) => prev.slice(0, -1));
        await loadSession(parentId, false);
      } else {
        setSession(null);
        setNodes([]);
        setStack([]);
      }
      await refreshList();
      return true;
    } finally {
      setBusy(false);
    }
  }, [session, sessionNotes, stack, loadSession, refreshList]);

  const leavePartial = useCallback(async () => {
    if (!session) {
      setSession(null);
      setNodes([]);
      setStack([]);
      return true;
    }
    if (session.status === "completed" || session.status === "abandoned") {
      setSession(null);
      setNodes([]);
      setStack([]);
      return true;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await abandonManufacturingBomAuditSession({
        session_id: session.id,
        as_partial: true,
        notes: sessionNotes,
      });
      if (!res.ok || !res.data.ok) {
        setError(errMsg(res as { ok: false; error?: unknown }));
        return false;
      }
      setSession(null);
      setNodes([]);
      setStack([]);
      await refreshList();
      return true;
    } finally {
      setBusy(false);
    }
  }, [session, sessionNotes, refreshList]);

  const abandon = useCallback(async () => {
    if (!session) return false;
    setBusy(true);
    setError(null);
    try {
      const res = await abandonManufacturingBomAuditSession({
        session_id: session.id,
        as_partial: false,
        notes: sessionNotes,
      });
      if (!res.ok || !res.data.ok) {
        setError(errMsg(res as { ok: false; error?: unknown }));
        return false;
      }
      setSession(null);
      setNodes([]);
      setStack([]);
      await refreshList();
      return true;
    } finally {
      setBusy(false);
    }
  }, [session, sessionNotes, refreshList]);

  const reopen = useCallback(
    async (sessionId: string) => {
      setBusy(true);
      setError(null);
      try {
        const res = await reopenManufacturingBomAuditSession(sessionId);
        if (!res.ok || !res.data.ok) {
          setError(errMsg(res as { ok: false; error?: unknown }));
          return false;
        }
        setSession(res.data.session);
        setNodes(res.data.nodes);
        setSessionNotes(res.data.session.notes ?? "");
        setStack([res.data.session.id]);
        await refreshList();
        return true;
      } finally {
        setBusy(false);
      }
    },
    [refreshList]
  );

  const goUp = useCallback(async () => {
    if (stack.length <= 1) return;
    const parentId = stack[stack.length - 2];
    setStack((prev) => prev.slice(0, -1));
    await loadSession(parentId, false);
  }, [stack, loadSession]);

  return {
    sessions,
    session,
    nodes,
    stack,
    busy,
    error,
    setError,
    sessionNotes,
    setSessionNotes,
    refreshList,
    loadSession,
    openAudit,
    saveNode,
    startSubAudit,
    complete,
    leavePartial,
    abandon,
    reopen,
    goUp,
  };
}
